import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { getJobCatalogBase, getJobDetailUrl } from './careersRoutes'
import { getAPIBaseURL, getUser } from '../services/helpers'
import { getData, postData } from '../services/request'
import ApplicationDocumentUpload, { getMissingMandatoryDocuments } from './ApplicationDocumentUpload'
import { employmentTypeLabel, locationTypeLabel } from '../utils/employmeLabels'

const questionTypeLabel = (t) => {
  const map = {
    YES_NO: 'Yes / No',
    TEXT: 'Short text',
    MULTIPLE_CHOICE: 'Multiple choice'
  }
  return map[t] || t || '—'
}

const formatDeadline = (d) => {
  if (!d) return null
  try {
    return new Date(d).toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return null
  }
}

const formatSalary = (range) => {
  if (!range) return null
  const min = range.min
  const max = range.max
  if (min == null && max == null) return null
  if (typeof min === 'number' && typeof max === 'number' && min === 0 && max === 0) {
    return null
  }
  const fmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })
  const cur = range.currency || 'NGN'
  if (min != null && max != null) {
    return `${fmt.format(min)} – ${fmt.format(max)} ${cur}`
  }
  if (min != null) return `From ${fmt.format(min)} ${cur}`
  if (max != null) return `Up to ${fmt.format(max)} ${cur}`
  return null
}

const formatCommuteCurrency = (amount, currency) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '—'
  const c = ['NGN', 'USD', 'EUR'].includes(currency) ? currency : 'NGN'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: c,
      maximumFractionDigits: 0
    }).format(amount)
  } catch {
    return `${Math.round(amount)} ${c}`
  }
}

const JobDetailsPage = () => {
  const { jobId } = useParams()
  const location = useLocation()
  const catalogBase = getJobCatalogBase(location.pathname)
  const jobUrl = (id) => getJobDetailUrl(id, location.pathname)
  const isLoggedIn =
    typeof window !== 'undefined' &&
    (sessionStorage.getItem('employme-active') === '1' || !!localStorage.getItem('employmeUser'))
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [applyLoading, setApplyLoading] = useState(false)
  const [applyMessage, setApplyMessage] = useState('')
  const [hasApplied, setHasApplied] = useState(false)
  const [applicationId, setApplicationId] = useState(null)
  const [applicationDocuments, setApplicationDocuments] = useState([])
  const [applicationStatus, setApplicationStatus] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [autoApplyPreview, setAutoApplyPreview] = useState(null)

  const [commuteLoading, setCommuteLoading] = useState(false)
  const [commuteError, setCommuteError] = useState('')
  const [commuteData, setCommuteData] = useState(null)

  const commuteEligible =
    job && (job.locationType === 'ONSITE' || job.locationType === 'HYBRID')

  useEffect(() => {
    setCommuteData(null)
    setCommuteError('')
  }, [jobId])

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true)
      setError('')
      const url = `${getAPIBaseURL()}/employme/jobs/${jobId}`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (reqError || !result || result.status !== 200) {
        const msg =
          result?.data?.data ||
          result?.data?.message ||
          'Unable to load this job. It may no longer be available.'
        setError(typeof msg === 'string' ? msg : 'Unable to load job')
        setJob(null)
        return
      }
      setJob(result.data.data)
    }
    fetchJob()
  }, [jobId])

  useEffect(() => {
    if (!isLoggedIn) return
    const fetchExistingApplication = async () => {
      const url = `${getAPIBaseURL()}/employme/jobseeker/applications`
      const { error: reqError, result } = await getData(url, {}, {})
      if (reqError || !result || result.status !== 200) {
        return
      }
      const apps = result.data.data || []
      const existing = apps.find((app) => {
        const id = app.jobId?._id || app.jobId
        return id && id.toString() === jobId
      })
      if (existing) {
        setHasApplied(true)
        setApplicationId(existing._id)
        setApplicationDocuments(existing.documents || [])
        setApplicationStatus(existing.status || '')
        setApplyMessage(
          'You have already applied for this job. You can track your status from your Applications page.'
        )
      }
    }
    fetchExistingApplication()
  }, [jobId, isLoggedIn])

  const skills = useMemo(() => {
    if (!job) return []
    if (Array.isArray(job.skills)) return job.skills.filter(Boolean)
    if (typeof job.skills === 'string') {
      return job.skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return []
  }, [job])

  const benefits = useMemo(() => {
    if (!job?.benefits) return []
    if (Array.isArray(job.benefits)) return job.benefits.filter(Boolean)
    if (typeof job.benefits === 'string') {
      return job.benefits
        .split(',')
        .map((b) => b.trim())
        .filter(Boolean)
    }
    return []
  }, [job])

  const eligibilityQuestions = job?.screening?.eligibilityQuestions || []
  const requiredDocuments = job?.screening?.requiredDocuments || []

  const locationParts = useMemo(() => {
    if (!job?.location) return []
    const { addressLine, city, state, country } = job.location
    return [addressLine, city, state, country].filter(Boolean)
  }, [job])

  const handleApply = async () => {
    if (hasApplied) {
      return
    }
    setError('')
    setApplyMessage('')
    setApplyLoading(true)
    const url = `${getAPIBaseURL()}/employme/jobseeker/applications`
    const payload = {
      jobId,
      eligibilityAnswers: [],
      documents: []
    }
    const { error: reqError, result } = await postData(url, {}, payload)
    setApplyLoading(false)
    if (reqError || !result) {
      setError(result?.data?.data || 'Unable to apply for this job')
      return
    }
    if (result.status === 201) {
      const created = result.data?.data
      const application = created?.application || created
      setApplicationId(application?._id || null)
      setApplicationDocuments(application?.documents || [])
      setApplyMessage(
        requiredDocuments.length > 0
          ? 'Application created. Upload the required documents below, then start your interview from Applications.'
          : 'Application created. You can continue your interview from your Applications page.'
      )
      setHasApplied(true)
      setApplicationStatus('APPLIED')
    } else {
      setError(result.data?.data || 'Unable to apply for this job')
    }
  }

  const handleDocumentUploaded = (doc) => {
    if (!doc?.label) return
    setApplicationDocuments((prev) => {
      const next = [...prev]
      const idx = next.findIndex(
        (d) => String(d.label || '').toLowerCase() === String(doc.label).toLowerCase()
      )
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...doc }
      } else {
        next.push(doc)
      }
      return next
    })
  }

  const missingMandatoryDocs = getMissingMandatoryDocuments(job, { documents: applicationDocuments })
  const canStartInterview = hasApplied && missingMandatoryDocs.length === 0

  const handleCommuteAnalysis = async () => {
    if (!jobId) return
    const user = getUser()
    if (!user || user.role !== 'JOB_SEEKER') {
      setCommuteError('Sign in as a job seeker to estimate commute cost and net pay.')
      return
    }
    setCommuteError('')
    setCommuteData(null)
    setCommuteLoading(true)
    const url = `${getAPIBaseURL()}/employme/jobseeker/jobs/${jobId}/commute-analysis`
    const { error: reqError, result } = await getData(url, {}, {})
    setCommuteLoading(false)
    if (reqError || !result) {
      const msg =
        result?.data?.data ||
        result?.data?.message ||
        'Unable to load commute analysis right now.'
      setCommuteError(typeof msg === 'string' ? msg : 'Unable to load commute analysis.')
      return
    }
    if (result.status === 200) {
      setCommuteData(result.data?.data || null)
    } else {
      const msg = result.data?.data || result.data?.message || 'Unable to load commute analysis.'
      setCommuteError(typeof msg === 'string' ? msg : 'Unable to load commute analysis.')
    }
  }

  const handleAutoApplyPreview = async () => {
    if (!jobId) return
    setPreviewError('')
    setAutoApplyPreview(null)
    setPreviewLoading(true)
    const url = `${getAPIBaseURL()}/employme/jobseeker/ai/auto-apply-preview`
    const payload = { jobId }
    const { error: reqError, result } = await postData(
      url,
      { 'Content-Type': 'application/json' },
      JSON.stringify(payload)
    )
    setPreviewLoading(false)
    if (reqError || !result) {
      setPreviewError(
        result?.data?.data || 'Unable to generate auto-apply preview right now.'
      )
      return
    }
    if (result.status === 200) {
      setAutoApplyPreview(result.data?.data || null)
    } else {
      setPreviewError(
        result.data?.data || 'Unable to generate auto-apply preview right now.'
      )
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-32 rounded bg-slate-200" />
          <div className="h-10 w-3/4 max-w-xl rounded bg-slate-200" />
          <div className="h-4 w-1/2 rounded bg-slate-200" />
          <div className="mt-8 h-40 rounded-xl bg-slate-100" />
          <div className="h-32 rounded-xl bg-slate-100" />
        </div>
      </div>
    )
  }

  if (!job) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link
          to={catalogBase}
          className="text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          ← Back to jobs
        </Link>
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-slate-700">
            {error || 'This job could not be found or is no longer listed.'}
          </p>
        </div>
      </div>
    )
  }

  const salaryText = formatSalary(job.salaryRange)
  const deadlineText = formatDeadline(job.applicationDeadline)
  const company = job.company
  const openForApplications = job.openForApplications !== false

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      <nav className="mb-6 text-sm text-slate-500">
        <Link to={catalogBase} className="font-medium text-blue-600 hover:text-blue-800">
          All jobs
        </Link>
        <span className="mx-2 text-slate-300">/</span>
        <span className="text-slate-700 line-clamp-1">{job.title}</span>
      </nav>

      {!openForApplications && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This job is listed for viewing only. Applications are not currently being accepted.
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {applyMessage && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {applyMessage}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex gap-4 min-w-0">
                {company?.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-xl object-cover ring-1 ring-slate-100"
                  />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-[#00A5E6] text-lg font-bold text-white">
                    {(company?.name || job.title || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                    {job.title}
                  </h1>
                  {company?.name && (
                    <p className="mt-1 text-base text-slate-600">{company.name}</p>
                  )}
                  {company?.industry && (
                    <p className="text-sm text-slate-500">{company.industry}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800">
                {employmentTypeLabel(job.employmentType)}
              </span>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800">
                {locationTypeLabel(job.locationType)}
              </span>
              {job.department && (
                <span className="inline-flex items-center rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800">
                  {job.department}
                </span>
              )}
              {job.gender && job.gender !== 'Any' && (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900">
                  Prefers {job.gender} applicants
                </span>
              )}
            </div>

            {locationParts.length > 0 && (
              <p className="mt-4 flex items-start gap-2 text-sm text-slate-600">
                <span className="mt-0.5 text-slate-400" aria-hidden>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </span>
                <span>{locationParts.join(' · ')}</span>
              </p>
            )}

            {company?.website && (
              <p className="mt-2 text-sm">
                <a
                  href={
                    company.website.startsWith('http')
                      ? company.website
                      : `https://${company.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-800"
                >
                  Company website
                </a>
              </p>
            )}
          </header>

          {company?.description && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">About the company</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 whitespace-pre-line">
                {company.description}
              </p>
            </section>
          )}

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">Role overview</h2>
            {job.description ? (
              <p className="mt-3 text-sm leading-relaxed text-slate-700 whitespace-pre-line">
                {job.description}
              </p>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No description provided.</p>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">Requirements</h2>
            {job.requirements ? (
              <p className="mt-3 text-sm leading-relaxed text-slate-700 whitespace-pre-line">
                {job.requirements}
              </p>
            ) : (
              <p className="mt-3 text-sm text-slate-500">No requirements listed.</p>
            )}
          </section>

          {skills.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Skills</h2>
              <ul className="mt-4 flex flex-wrap gap-2">
                {skills.map((s) => (
                  <li
                    key={s}
                    className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-800"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {benefits.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Benefits</h2>
              <ul className="mt-4 list-inside list-disc space-y-2 text-sm text-slate-700">
                {benefits.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </section>
          )}

          {(eligibilityQuestions.length > 0 || requiredDocuments.length > 0) && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Application screening</h2>
              <p className="mt-1 text-sm text-slate-500">
                You may need to answer these and upload documents when you apply or during the
                process.
              </p>

              {eligibilityQuestions.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-sm font-semibold text-slate-800">Eligibility questions</h3>
                  <ul className="mt-3 space-y-3">
                    {eligibilityQuestions.map((q, index) => (
                      <li
                        key={index}
                        className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3 text-sm"
                      >
                        <p className="font-medium text-slate-900">{q.question || 'Question'}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Type: {questionTypeLabel(q.type)}
                        </p>
                        {Array.isArray(q.options) && q.options.length > 0 && (
                          <p className="mt-2 text-xs text-slate-600">
                            Options: {q.options.join(' · ')}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {requiredDocuments.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-800">Documents</h3>
                  <ul className="mt-3 space-y-2">
                    {requiredDocuments.map((doc, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 text-sm text-slate-700"
                      >
                        <span>{doc.label || 'Document'}</span>
                        {doc.isMandatory && (
                          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                            Required
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {hasApplied && applicationId && requiredDocuments.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Upload your documents</h2>
              <p className="mt-1 text-sm text-slate-500">
                Upload the documents requested by the employer before starting your interview.
              </p>
              {missingMandatoryDocs.length > 0 && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Still required: {missingMandatoryDocs.map((d) => d.label).join(', ')}
                </p>
              )}
              <div className="mt-5 space-y-3">
                {requiredDocuments.map((doc, index) => {
                  const uploaded = applicationDocuments.find(
                    (d) =>
                      String(d.label || '').toLowerCase() ===
                      String(doc.label || '').toLowerCase()
                  )
                  return (
                    <ApplicationDocumentUpload
                      key={`${doc.label}-${index}`}
                      applicationId={applicationId}
                      label={doc.label}
                      isMandatory={doc.isMandatory !== false}
                      uploadedDoc={uploaded}
                      onUploaded={handleDocumentUploaded}
                    />
                  )
                })}
              </div>
              {canStartInterview && applicationId && (
                <div className="mt-5">
                  <Link
                    to={`/jobs/applications/${applicationId}/interview`}
                    className="inline-flex rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700"
                  >
                    Start interview
                  </Link>
                </div>
              )}
            </section>
          )}

          {(job.interviewTimeLimitSeconds != null ||
            job.interviewMaxTakes != null ||
            (Array.isArray(job?.interviewQuestions) && job.interviewQuestions.length > 0)) && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Video interview</h2>
              <p className="mt-1 text-sm text-slate-500">
                After you apply, you may be asked to record short video answers.
              </p>
              {isLoggedIn && (
                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  {job.interviewTimeLimitSeconds != null && (
                    <div className="rounded-xl bg-slate-50 px-4 py-3">
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Time per question
                      </dt>
                      <dd className="mt-1 font-semibold text-slate-900">
                        {job.interviewTimeLimitSeconds}s
                      </dd>
                    </div>
                  )}
                  {job.interviewMaxTakes != null && (
                    <div className="rounded-xl bg-slate-50 px-4 py-3">
                      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Max takes
                      </dt>
                      <dd className="mt-1 font-semibold text-slate-900">{job.interviewMaxTakes}</dd>
                    </div>
                  )}
                </dl>
              )}
            </section>
          )}

          {commuteEligible && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Commute &amp; take-home estimate</h2>
              <p className="mt-1 text-sm text-slate-600">
                For on-site and hybrid roles we estimate one-way distance from your profile pin, typical
                monthly transport, and (when salary is visible) what is left after commuting.
              </p>
              {(!getUser() || getUser()?.role !== 'JOB_SEEKER') && (
                <p className="mt-2 text-sm text-slate-600">
                  <Link to="/login" className="font-medium text-blue-600 hover:text-blue-800">
                    Sign in
                  </Link>{' '}
                  as a job seeker to run this estimate.
                </p>
              )}
              <button
                type="button"
                onClick={handleCommuteAnalysis}
                disabled={commuteLoading || !getUser() || getUser()?.role !== 'JOB_SEEKER'}
                className="mt-4 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {commuteLoading ? 'Calculating…' : 'Estimate commute impact'}
              </button>
              {commuteError && (
                <p className="mt-3 text-sm text-red-700">{commuteError}</p>
              )}
              {commuteData?.applicable === false && commuteData?.message && (
                <p className="mt-3 text-sm text-slate-700">{commuteData.message}</p>
              )}
              {commuteData?.needsCandidateLocation && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
                  <p className="font-medium">{commuteData.message}</p>
                  <Link
                    to="/jobs/profile"
                    className="mt-2 inline-block font-semibold text-amber-900 underline hover:text-amber-950"
                  >
                    Open profile and set your location on the map
                  </Link>
                </div>
              )}
              {commuteData?.needsJobLocation && (
                <p className="mt-3 text-sm text-slate-600">{commuteData.message}</p>
              )}
              {commuteData?.applicable === true &&
                commuteData.distanceKm != null &&
                !commuteData.needsCandidateLocation &&
                !commuteData.needsJobLocation && (
                  <div className="mt-4 space-y-3 rounded-xl border border-slate-100 bg-slate-50/90 p-4 text-sm text-slate-800">
                    {commuteData.costEstimateSource && (
                      <p className="text-xs text-slate-500">
                        Cost per km:{' '}
                        {commuteData.costEstimateSource === 'ai'
                          ? 'estimated by AI from the job’s country/region'
                          : 'default rate (AI unavailable)'}
                        {commuteData.costPerKmOneWay != null && commuteData.currency
                          ? ` — ~${Number(commuteData.costPerKmOneWay).toFixed(2)} ${commuteData.currency}/km one-way`
                          : ''}
                        .
                      </p>
                    )}
                    <dl className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          One-way distance
                        </dt>
                        <dd className="mt-0.5 font-semibold text-slate-900">
                          {commuteData.distanceKm.toFixed(1)} km
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Est. monthly transport
                        </dt>
                        <dd className="mt-0.5 font-semibold text-slate-900">
                          {formatCommuteCurrency(
                            commuteData.monthlyTransportCost ?? commuteData.monthlyTransportCostNgN,
                            commuteData.currency || 'NGN'
                          )}
                        </dd>
                      </div>
                      {commuteData.officeDaysPerMonthAssumption != null && (
                        <div className="sm:col-span-2">
                          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Office commute days assumed / month
                          </dt>
                          <dd className="mt-0.5 font-semibold text-slate-900">
                            {commuteData.officeDaysPerMonthAssumption}
                          </dd>
                        </div>
                      )}
                    </dl>
                    {commuteData.salaryComparison?.disclosed &&
                      commuteData.salaryComparison?.estimatedNetAfterTransportMonthly && (
                        <div className="rounded-lg bg-white px-3 py-2 text-slate-700 ring-1 ring-slate-100">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            After transport (monthly band)
                          </p>
                          <p className="mt-1 font-semibold text-slate-900">
                            {formatCommuteCurrency(
                              commuteData.salaryComparison.estimatedNetAfterTransportMonthly.min,
                              commuteData.salaryComparison.currency || commuteData.currency || 'NGN'
                            )}{' '}
                            –{' '}
                            {formatCommuteCurrency(
                              commuteData.salaryComparison.estimatedNetAfterTransportMonthly.max,
                              commuteData.salaryComparison.currency || commuteData.currency || 'NGN'
                            )}
                          </p>
                          {commuteData.salaryComparison.transportAsPercentOfMinSalary != null && (
                            <p className="mt-1 text-xs text-slate-600">
                              Transport is about{' '}
                              {commuteData.salaryComparison.transportAsPercentOfMinSalary}% of the
                              lower salary figure.
                            </p>
                          )}
                        </div>
                      )}
                    {commuteData.advice && (
                      <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">
                        {commuteData.advice}
                      </p>
                    )}
                  </div>
                )}
            </section>
          )}

        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-6 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                At a glance
              </h2>
              <dl className="mt-4 space-y-4 text-sm">
                {deadlineText && (
                  <div>
                    <dt className="text-slate-500">Apply by</dt>
                    <dd className="mt-0.5 font-semibold text-slate-900">{deadlineText}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-slate-500">Employment</dt>
                  <dd className="mt-0.5 font-semibold text-slate-900">
                    {employmentTypeLabel(job.employmentType)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Work setup</dt>
                  <dd className="mt-0.5 font-semibold text-slate-900">
                    {locationTypeLabel(job.locationType)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Salary</dt>
                  <dd className="mt-0.5 font-semibold text-slate-900">
                    {salaryText || 'Not disclosed'}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 space-y-3">
                {!openForApplications ? (
                  <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm text-slate-600">
                    Applications are closed for this role.
                  </p>
                ) : !isLoggedIn ? (
                  <>
                    <Link
                      to="/login"
                      state={{ from: jobUrl(jobId) }}
                      className="block w-full rounded-xl bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    >
                      Sign in to apply
                    </Link>
                    <Link
                      to="/signup"
                      state={{ from: jobUrl(jobId) }}
                      className="block w-full rounded-xl border border-slate-200 bg-white py-2.5 text-center text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Create free account
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleApply}
                      disabled={applyLoading || hasApplied}
                      className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {hasApplied
                        ? 'Already applied'
                        : applyLoading
                          ? 'Applying…'
                          : 'Apply and start interview'}
                    </button>
                    {hasApplied && applicationStatus && (
                      <p className="text-center text-xs text-slate-600">
                        Status:{' '}
                        <span className="font-semibold text-slate-900">{applicationStatus}</span>
                      </p>
                    )}
                    {hasApplied && missingMandatoryDocs.length > 0 && (
                      <p className="text-center text-xs text-amber-800">
                        Upload required documents below before starting the interview.
                      </p>
                    )}
                    {canStartInterview && applicationId && (
                      <Link
                        to={`/jobs/applications/${applicationId}/interview`}
                        className="block w-full rounded-xl bg-amber-600 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-amber-700"
                      >
                        Start interview
                      </Link>
                    )}
                    <Link
                      to="/jobs/applications"
                      className="block w-full rounded-xl border border-slate-200 bg-white py-2.5 text-center text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      View my applications
                    </Link>
                  </>
                )}
              </div>

              {isLoggedIn && (
              <div className="mt-6 border-t border-slate-100 pt-5">
                <button
                  type="button"
                  onClick={handleAutoApplyPreview}
                  disabled={previewLoading}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-100 disabled:opacity-60"
                >
                  {previewLoading ? 'Preparing preview…' : 'AI auto-apply preview'}
                </button>
                {previewError && (
                  <p className="mt-2 text-xs text-red-600">{previewError}</p>
                )}
                {autoApplyPreview && (
                  <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-900">
                      AI auto-apply preview
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      Suggestions from your profile and this job. Review before using elsewhere.
                    </p>
                    {autoApplyPreview.shortPitch && (
                      <div className="mt-3">
                        <h3 className="text-xs font-semibold text-slate-800">Suggested short pitch</h3>
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-700 whitespace-pre-line">
                          {autoApplyPreview.shortPitch}
                        </p>
                      </div>
                    )}
                    {Array.isArray(autoApplyPreview.eligibilityAnswers) &&
                      autoApplyPreview.eligibilityAnswers.length > 0 && (
                        <div className="mt-4">
                          <h3 className="text-xs font-semibold text-slate-800">
                            Suggested eligibility answers
                          </h3>
                          <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto">
                            {autoApplyPreview.eligibilityAnswers.map((item, index) => (
                              <li
                                key={index}
                                className="rounded-lg bg-white/80 px-3 py-2 text-xs shadow-sm"
                              >
                                {item.question && (
                                  <p className="font-medium text-slate-900">Q: {item.question}</p>
                                )}
                                {item.suggestedAnswer && (
                                  <p className="mt-1.5 whitespace-pre-line text-slate-700">
                                    A: {item.suggestedAnswer}
                                  </p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                )}
              </div>
              )}
            </div>

            {(eligibilityQuestions.length > 0 || requiredDocuments.length > 0) && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 text-sm text-slate-600">
                <p className="font-medium text-slate-800">Before you apply</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  {requiredDocuments.length > 0 && (
                    <li>
                      {requiredDocuments.length} document
                      {requiredDocuments.length !== 1 ? 's' : ''} may be required
                    </li>
                  )}
                  {eligibilityQuestions.length > 0 && (
                    <li>{eligibilityQuestions.length} screening question(s)</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}

export default JobDetailsPage
